import { CalendarDialog } from "@/components/CalendarDialog";
import { motion } from "framer-motion";
import { Instagram, Facebook, MapPin, Phone, Mail } from "lucide-react";
import fullscaleLogoWhite from "@/assets/fullscale-logo-white.png";
const footerLinks = [{
  label: "Home",
  href: "#hero-section"
}, {
  label: "Our System",
  href: "#system-section"
}, {
  label: "Results",
  href: "#results-section"
}];
const moreLinks = [{
  label: "Our Process",
  href: "#process-section"
}, {
  label: "FAQs",
  href: "#faq-section"
}, {
  label: "Contact Us",
  href: "#contact-section"
}];
const serviceAreas = ["Sydney", "Melbourne", "Brisbane", "Perth", "Adelaide", "Gold Coast", "Newcastle", "Canberra"];
const industries = ["Electricians", "Plumbers", "Builders", "Roofers", "HVAC", "Landscapers", "Painters", "Pool Builders"];
export function Footer() {
  const currentYear = new Date().getFullYear();
  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth"
      });
    }
  };
  return <footer className="bg-[hsl(160_10%_6%)] relative" role="contentinfo" aria-label="Site footer">
      {/* Top decorative separator */}
      <div className="h-10 sm:h-12 bg-gradient-to-b from-background-dark to-[hsl(160_10%_6%)] relative">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
      </div>
      
      {/* Footer CTA Section */}
      <div className="py-10 sm:py-12 lg:py-14 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(160_10%_8%)] to-[hsl(160_10%_5%)]" />
        <div className="absolute inset-0 grid-pattern-dark opacity-20" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] sm:w-[700px] h-[200px] sm:h-[350px] bg-primary/[0.06] rounded-full blur-[100px] sm:blur-[150px]" />
        
        <motion.div initial={{
        opacity: 0,
        y: 32
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1]
      }} className="container-wide text-center relative px-4">
          <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-foreground-dark mb-3">
            Ready to Grow Your Business in Australia?
          </h3>
          <p className="text-foreground-dark/60 mb-6 max-w-xl mx-auto text-sm sm:text-base">
            Book a free strategy call and discover how our digital growth system can help you get more leads, reviews and jobs.
          </p>
          <CalendarDialog buttonVariant="dark-primary" buttonSize="default" buttonClassName="h-12 sm:h-14 px-8 sm:px-10 text-sm sm:text-base font-bold rounded-xl animate-pulse-soft shadow-[0_8px_32px_-8px_hsl(98_58%_38%/0.5)]" />
        </motion.div>
      </div>

      {/* Separator */}
      <div className="relative h-px">
        <div className="absolute inset-x-0 h-px bg-gradient-to-r from-transparent via-foreground-dark/20 to-transparent" />
      </div>

      {/* Main Footer */}
      <div className="py-14 sm:py-16 bg-[hsl(160_10%_4%)]">
        <div className="container-wide">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
            {/* Brand Column */}
            <div className="md:col-span-5 lg:col-span-4">
              <a href="#hero-section" onClick={e => {
              e.preventDefault();
              scrollToSection("#hero-section");
            }} className="inline-block mb-5" aria-label="FullScale Marketing - Back to top">
                <img src={fullscaleLogoWhite} alt="FullScale Marketing - Digital Marketing Agency for Tradies Australia" className="h-8 sm:h-10 w-auto" loading="lazy" />
              </a>
              <p className="text-foreground-dark/60 max-w-md text-[15px] leading-relaxed mb-4">
                Australia's leading digital marketing agency for trade, construction & home service businesses. We help you get found, build trust, and win more high-value jobs.
              </p>
              
              {/* Business Category Badge */}
              <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-lg px-3 py-2 mb-4">
                <span className="text-primary text-xs font-semibold">Digital Marketing Agency</span>
                <span className="text-foreground-dark/40 text-xs">•</span>
                <span className="text-foreground-dark/60 text-xs">Trade & Construction Specialists</span>
              </div>
            </div>

            {/* Quick Links */}
            <div className="md:col-span-2 lg:col-span-2">
              <h4 className="font-semibold text-foreground-dark mb-5 text-[15px]">Quick Links</h4>
              <nav className="space-y-3" aria-label="Quick links">
                {footerLinks.map(link => <a key={link.href} href={link.href} onClick={e => {
                e.preventDefault();
                scrollToSection(link.href);
              }} className="block text-foreground-dark/60 hover:text-primary transition-colors duration-300 text-[15px]">
                    {link.label}
                  </a>)}
              </nav>
            </div>

            {/* More Links */}
            <div className="md:col-span-2 lg:col-span-2">
              <h4 className="font-semibold text-foreground-dark mb-5 text-[15px]">More</h4>
              <nav className="space-y-3" aria-label="Additional links">
                {moreLinks.map(link => <a key={link.href} href={link.href} onClick={e => {
                e.preventDefault();
                scrollToSection(link.href);
              }} className="block text-foreground-dark/60 hover:text-primary transition-colors duration-300 text-[15px]">
                    {link.label}
                  </a>)}
              </nav>
            </div>

            {/* Service Areas */}
            <div className="md:col-span-3 lg:col-span-2">
              <h4 className="font-semibold text-foreground-dark mb-5 text-[15px]">Service Areas</h4>
              <div className="flex flex-wrap gap-1.5">
                {serviceAreas.map(area => <span key={area} className="text-foreground-dark/50 text-[13px] bg-foreground-dark/5 px-2 py-1 rounded">
                    {area}
                  </span>)}
              </div>
              <p className="text-foreground-dark/40 text-xs mt-3">+ All regional areas Australia-wide</p>
            </div>

            {/* Connect */}
            <div className="md:col-span-12 lg:col-span-2">
              <h4 className="font-semibold text-foreground-dark mb-5 text-[15px]">Connect</h4>
              <div className="flex items-center gap-4 mb-4">
                <a href="https://www.instagram.com/fullscale_marketing/" target="_blank" rel="noopener noreferrer" className="text-foreground-dark/60 hover:text-foreground-dark transition-colors duration-300" aria-label="Follow FullScale Marketing on Instagram">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="https://www.facebook.com/p/FullScale-Marketing-61562089223781/" target="_blank" rel="noopener noreferrer" className="text-foreground-dark/60 hover:text-foreground-dark transition-colors duration-300" aria-label="Follow FullScale Marketing on Facebook">
                  <Facebook className="w-5 h-5" />
                </a>
              </div>
              
              {/* Contact Info */}
              <div className="space-y-2 text-foreground-dark/50 text-[13px]">
                <div className="flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>Australia-wide</span>
                </div>
                <a href="mailto:info@fullscalemarketing.com.au" className="flex items-center gap-2 hover:text-foreground-dark transition-colors">
                  <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>info@fullscalemarketing.com.au</span>
                </a>
              </div>
            </div>
          </div>

          {/* Industries We Serve */}
          <div className="mt-10 pt-8 border-t border-foreground-dark/10">
            <h4 className="font-semibold text-foreground-dark mb-4 text-sm text-center">
              Industries We Serve
            </h4>
            <div className="flex flex-wrap justify-center gap-2">
              {industries.map(industry => <span key={industry} className="text-foreground-dark/40 text-xs">
                  {industry}
                </span>)}
              <span className="text-foreground-dark/40 text-xs">& More</span>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-10 mt-8 border-t border-foreground-dark/10 flex flex-col items-center gap-4">
            <p className="text-sm text-foreground-dark/50 text-center">
              © {currentYear} FullScale Marketing. All rights reserved.
            </p>
            <div className="flex items-center gap-8 text-sm text-foreground-dark/50">
              <a href="#" className="hover:text-foreground-dark transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="hover:text-foreground-dark transition-colors">
                Terms of Service
              </a>
            </div>
          </div>

          {/* SEO Footer Text */}
          <div className="mt-8 text-center">
            <p className="text-foreground-dark/30 text-xs leading-relaxed max-w-3xl mx-auto">
              FullScale Marketing is Australia's specialist digital marketing agency for tradies, construction companies and home service businesses. We provide local SEO, Google Ads, Facebook Ads, website design, Google review systems and lead generation services across Sydney, Melbourne, Brisbane, Perth, Adelaide, Gold Coast, Newcastle, Canberra and all regional areas.
            </p>
          </div>
        </div>
      </div>
    </footer>;
}